import React, { useEffect, useMemo, useState } from 'react'
import posthog from 'posthog-js'

const WEEKS_PER_MONTH = 52 / 12

const safeCapture = (eventName, properties) => {
  try {
    if (posthog && typeof posthog.capture === 'function') {
      posthog.capture(eventName, properties)
    }
  } catch {
    // Analytics must never break the tool.
  }
}

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

const normalizeToMonthly = (amount, frequency) => {
  if (!Number.isFinite(amount) || amount < 0) return 0
  return frequency === 'yearly' ? amount / 12 : amount
}

const parseOptionalNonNegativeNumber = (rawValue) => {
  const sanitized = String(rawValue ?? '')
    .trim()
    .replace(',', '.')
    .replace(/[^0-9.]/g, '')
    .replace(/(\..*)\./g, '$1')

  if (sanitized === '') return null

  const parsed = Number(sanitized)
  if (!Number.isFinite(parsed)) return null

  return Math.max(0, parsed)
}

const normalizeNumberInputValue = (value) => (Number.isFinite(value) ? String(value) : '')

const formatMoney = (amount, currency) => {
  if (!Number.isFinite(amount)) return '-'
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount)
  } catch {
    return `${amount.toFixed(2)} ${currency || ''}`.trim()
  }
}

const currencyPrefix = (currencyCode) => {
  if (!currencyCode) return ''
  try {
    const parts = new Intl.NumberFormat(undefined, { style: 'currency', currency: currencyCode, currencyDisplay: 'narrowSymbol' }).formatToParts(0)
    const symbol = parts.find((p) => p.type === 'currency')?.value
    return symbol || currencyCode
  } catch {
    return currencyCode
  }
}

const cardClass = 'breakeven-card overflow-hidden bg-marketing-paper'
const cardBodyClass = 'px-4 py-5 sm:px-6 lg:px-8'
const labelClass = 'mb-2 block text-sm font-semibold text-marketing-forest'
const textInputClass = 'w-full rounded-xl border border-marketing-line bg-marketing-paper px-3 py-2.5 text-sm text-marketing-forest shadow-sm outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20'
const inputGroupClass = 'flex items-stretch overflow-hidden rounded-xl border border-marketing-line bg-marketing-paper shadow-sm transition focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20'
const inputGroupFieldClass = 'min-w-0 flex-1 border-0 bg-marketing-paper px-3 py-2.5 text-sm text-marketing-forest outline-none'
const inputGroupAddonClass = 'inline-flex items-center bg-marketing-ivory px-3 text-sm font-medium text-marketing-muted'

const Stepper = ({ label, value, onChange, min = 0, max = 1000, step = 1, iconClass = null }) => (
  <div className="flex items-center justify-between gap-3 rounded-2xl border border-marketing-line bg-marketing-ivory p-3">
    <div className="flex min-w-0 items-center gap-3">
      {iconClass ? (
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-marketing-paper text-primary shadow-sm">
          <i className={iconClass} aria-hidden="true" />
        </span>
      ) : null}
      <span className="text-sm font-semibold text-marketing-forest sm:text-base">{label}</span>
    </div>
    <div className="flex items-center gap-2">
      <button
        type="button"
        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-marketing-line bg-marketing-paper text-marketing-muted transition-colors hover:border-primary/30 hover:text-primary"
        onClick={() => onChange(clamp(value - step, min, max))}
        aria-label={`Decrease ${label}`}
      >
        <i className="fa-solid fa-minus text-xs" aria-hidden="true" />
      </button>
      <div className="w-14 text-center text-base font-bold text-marketing-forest">
        {value}
      </div>
      <button
        type="button"
        className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-marketing-line bg-marketing-paper text-marketing-muted transition-colors hover:border-primary/30 hover:text-primary"
        onClick={() => onChange(clamp(value + step, min, max))}
        aria-label={`Increase ${label}`}
      >
        <i className="fa-solid fa-plus text-xs" aria-hidden="true" />
      </button>
    </div>
  </div>
)

const BreakEvenCalculator = ({ content = {}, locale = 'en' }) => {
  const copy = content || {}
  const heroCopy = copy.hero || {}
  const cardsCopy = copy.cards || {}
  const actionsCopy = copy.actions || {}
  const resultsCopy = copy.results || {}
  const sharedCopy = copy.shared || {}
  const currencies = sharedCopy.currencies || {}

  const currencyOptions = useMemo(() => {
    if (currencies && typeof currencies === 'object') {
      return Object.entries(currencies)
    }
    return []
  }, [currencies])

  const defaultCurrency = useMemo(() => {
    if (currencyOptions.some(([code]) => code === 'USD')) return 'USD'
    if (currencyOptions.length > 0) return currencyOptions[0][0]
    return 'USD'
  }, [currencyOptions])

  const [currency, setCurrency] = useState(defaultCurrency)

  useEffect(() => {
    setCurrency((current) => {
      if (currencyOptions.some(([code]) => code === current)) return current
      return defaultCurrency
    })
  }, [currencyOptions, defaultCurrency])

  const [showAdvancedSchedule, setShowAdvancedSchedule] = useState(false)
  const [showAdvancedFixed, setShowAdvancedFixed] = useState(false)

  const [hasCalculated, setHasCalculated] = useState(false)
  const [result, setResult] = useState(null)

  const [formData, setFormData] = useState({
    classesPerWeek: 15,
    avgAttendance: 8,
    classDurationMinutes: 50,
    openHoursPerWeek: 40,
    instructorPayModel: 'percentage',
    instructorSplitPercent: 50,
    instructorPayPerClass: 100,
    rentAmount: 2500,
    rentFrequency: 'monthly',
    softwareAmount: 150,
    softwareFrequency: 'monthly',
    additionalOverhead: []
  })

  useEffect(() => {
    safeCapture('tool_viewed', { tool: 'breakeven_calculator', locale })
  }, [locale])

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const addOverheadItem = () => {
    setFormData((prev) => ({
      ...prev,
      additionalOverhead: [
        ...prev.additionalOverhead,
        { id: `overhead-${Math.random().toString(36).slice(2, 10)}`, label: '', amount: 0, frequency: 'monthly' }
      ]
    }))
  }

  const updateOverheadItem = (id, field, value) => {
    setFormData((prev) => ({
      ...prev,
      additionalOverhead: prev.additionalOverhead.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    }))
  }

  const removeOverheadItem = (id) => {
    setFormData((prev) => ({
      ...prev,
      additionalOverhead: prev.additionalOverhead.filter((item) => item.id !== id)
    }))
  }

  const calculate = () => {
    const classesPerMonth = formData.classesPerWeek * WEEKS_PER_MONTH
    if (!classesPerMonth || classesPerMonth <= 0) return

    const monthlyRent = normalizeToMonthly(formData.rentAmount, formData.rentFrequency)
    const monthlySoftware = normalizeToMonthly(formData.softwareAmount, formData.softwareFrequency)
    const monthlyAdditional = formData.additionalOverhead.reduce((sum, item) => sum + normalizeToMonthly(item.amount, item.frequency), 0)
    const totalFixed = monthlyRent + monthlySoftware + monthlyAdditional

    const fixedCostPerClass = totalFixed / classesPerMonth

    let costPerClass = 0
    let instructorPayPerClassEffective = 0

    if (formData.instructorPayModel === 'fixed') {
      instructorPayPerClassEffective = Number.isFinite(formData.instructorPayPerClass) ? formData.instructorPayPerClass : 0
      costPerClass = fixedCostPerClass + instructorPayPerClassEffective
    } else {
      const split = (Number.isFinite(formData.instructorSplitPercent) ? formData.instructorSplitPercent : 0) / 100
      if (split <= 0 || split >= 1) return
      costPerClass = fixedCostPerClass / (1 - split)
      instructorPayPerClassEffective = costPerClass * split
    }

    const monthlyInstructorCost = instructorPayPerClassEffective * classesPerMonth
    const totalMonthlyCost = totalFixed + monthlyInstructorCost
    const breakEvenPerVisit = formData.avgAttendance > 0 ? costPerClass / formData.avgAttendance : 0

    const teachingHoursPerWeek = (formData.classesPerWeek * formData.classDurationMinutes) / 60
    const deadHoursPerWeek = Math.max(0, formData.openHoursPerWeek - teachingHoursPerWeek)
    const utilizationRate = formData.openHoursPerWeek > 0 ? (teachingHoursPerWeek / formData.openHoursPerWeek) * 100 : 0
    const deadHoursCostMonthly = totalFixed * (1 - utilizationRate / 100)

    setResult({
      costPerClass,
      breakEvenPerVisit,
      monthlyFixedCosts: totalFixed,
      monthlyInstructorCost,
      monthlyTotalCost: totalMonthlyCost,
      deadHoursPerWeek,
      deadHoursCostMonthly,
      utilizationRate
    })
    setHasCalculated(true)
  }

  useEffect(() => {
    if (!hasCalculated) return
    calculate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData])

  const perClassFixed = useMemo(() => {
    if (!result) return 0
    const classesPerMonth = formData.classesPerWeek * WEEKS_PER_MONTH
    if (!classesPerMonth) return 0
    return result.monthlyFixedCosts / classesPerMonth
  }, [result, formData.classesPerWeek])

  const perClassInstructor = useMemo(() => {
    if (!result) return 0
    const classesPerMonth = formData.classesPerWeek * WEEKS_PER_MONTH
    if (!classesPerMonth) return 0
    return (result.monthlyInstructorCost || 0) / classesPerMonth
  }, [result, formData.classesPerWeek])

  const fixedShare = result?.monthlyTotalCost ? (result.monthlyFixedCosts / result.monthlyTotalCost) * 100 : 0
  const instructorShare = result?.monthlyTotalCost ? (result.monthlyInstructorCost / result.monthlyTotalCost) * 100 : 0

  const handleCalculateClick = () => {
    calculate()
    safeCapture('tool_calculated', { tool: 'breakeven_calculator', currency, completed_advanced: showAdvancedSchedule || showAdvancedFixed })
  }

  const scrollToResults = () => {
    setTimeout(() => {
      document.getElementById('breakeven-results')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 100)
  }

  const handleMobileCalculate = () => {
    handleCalculateClick()
    scrollToResults()
  }

  return (
    <section className="breakeven-calculator-page pb-16 pt-16 lg:pb-24 lg:pt-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto mb-10 max-w-4xl text-center lg:mb-14">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2 text-sm font-semibold text-marketing-forest">
            <span className="rounded-full border border-primary/20 bg-marketing-paper px-3 py-1 text-xs font-extrabold uppercase tracking-[0.12em] text-primary">
              {heroCopy.badge}
            </span>
            <span>{heroCopy.eyebrow}</span>
          </div>
          <h1 className="mt-5 font-display text-5xl font-normal leading-none tracking-[-0.045em] text-marketing-forest sm:text-7xl lg:text-8xl">
            {heroCopy.title}
          </h1>
          <p className="mx-auto mt-5 max-w-3xl text-base leading-8 text-marketing-muted sm:text-lg">
            {heroCopy.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-4 xl:gap-5 lg:grid-cols-[minmax(0,1.15fr)_minmax(320px,0.85fr)]">
          <div className="space-y-4">
            <div className={cardClass}>
              <div className={cardBodyClass}>
                <div className="mb-4 flex items-center gap-3">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary text-white">
                    <i className="fa-regular fa-clock" aria-hidden="true" />
                  </span>
                  <h2 className="text-xl font-semibold tracking-tight text-marketing-forest">
                    {cardsCopy.schedule?.title}
                  </h2>
                </div>

                <div className="space-y-3">
                  <Stepper
                    label={cardsCopy.schedule?.classes_per_week_label}
                    value={formData.classesPerWeek}
                    onChange={(val) => updateField('classesPerWeek', val)}
                    min={0}
                    max={200}
                    iconClass="fa-regular fa-calendar"
                  />
                  <Stepper
                    label={cardsCopy.schedule?.avg_attendance_label}
                    value={formData.avgAttendance}
                    onChange={(val) => updateField('avgAttendance', val)}
                    min={0}
                    max={200}
                    iconClass="fa-solid fa-users"
                  />

                  <button
                    type="button"
                    className="inline-flex items-center gap-2 rounded-full px-1 py-1 text-sm font-semibold text-primary transition-colors hover:text-primary-700"
                    onClick={() => setShowAdvancedSchedule((value) => !value)}
                  >
                    <span>{showAdvancedSchedule ? cardsCopy.schedule?.hide_details : cardsCopy.schedule?.show_details}</span>
                    <i className={`fa-solid ${showAdvancedSchedule ? 'fa-chevron-up' : 'fa-chevron-down'} text-xs`} aria-hidden="true" />
                  </button>

                  {showAdvancedSchedule ? (
                    <div className="space-y-3 border-t border-slate-200 pt-3">
                      <Stepper
                        label={cardsCopy.schedule?.class_duration_label}
                        value={formData.classDurationMinutes}
                        onChange={(val) => updateField('classDurationMinutes', val)}
                        min={0}
                        max={240}
                        step={5}
                      />
                      <Stepper
                        label={cardsCopy.schedule?.open_hours_label}
                        value={formData.openHoursPerWeek}
                        onChange={(val) => updateField('openHoursPerWeek', val)}
                        min={0}
                        max={168}
                        step={5}
                      />
                      <p className="text-sm text-slate-500">{cardsCopy.schedule?.details_help}</p>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            <div className={cardClass}>
              <div className={cardBodyClass}>
                <div className="mb-4 flex items-center gap-3">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary text-white">
                    <i className="fa-solid fa-users" aria-hidden="true" />
                  </span>
                  <h2 className="text-xl font-semibold tracking-tight text-marketing-forest">
                    {cardsCopy.payroll?.title}
                  </h2>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className={labelClass}>{cardsCopy.payroll?.currency_label}</label>
                    <select className={textInputClass} value={currency} onChange={(event) => setCurrency(event.target.value)} aria-label={cardsCopy.payroll?.currency_label}>
                      {currencyOptions.map(([code, label]) => (
                        <option key={code} value={code}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className={labelClass}>{cardsCopy.payroll?.instructor_pay_model_label}</label>
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-1">
                      <div className="grid grid-cols-2 gap-1">
                        {[
                          ['percentage', cardsCopy.payroll?.instructor_pay_model_percentage],
                          ['fixed', cardsCopy.payroll?.instructor_pay_model_fixed]
                        ].map(([value, label]) => (
                          <button
                            key={value}
                            type="button"
                            className={`rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${
                              formData.instructorPayModel === value
                                ? 'bg-white text-slate-900 shadow-sm'
                                : 'text-slate-600 hover:text-slate-900'
                            }`}
                            onClick={() => updateField('instructorPayModel', value)}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className={labelClass}>
                      {formData.instructorPayModel === 'percentage' ? cardsCopy.payroll?.instructor_split_label : cardsCopy.payroll?.instructor_fixed_pay_label}
                    </label>
                    {formData.instructorPayModel === 'percentage' ? (
                      <div className={inputGroupClass}>
                        <input
                          type="text"
                          inputMode="decimal"
                          className={inputGroupFieldClass}
                          value={normalizeNumberInputValue(formData.instructorSplitPercent)}
                          onChange={(event) => updateField('instructorSplitPercent', parseOptionalNonNegativeNumber(event.target.value))}
                        />
                        <span className={`${inputGroupAddonClass} border-l border-slate-200`}>%</span>
                      </div>
                    ) : (
                      <div className={inputGroupClass}>
                        <span className={`${inputGroupAddonClass} border-r border-slate-200`}>{currencyPrefix(currency)}</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          className={inputGroupFieldClass}
                          value={normalizeNumberInputValue(formData.instructorPayPerClass)}
                          onChange={(event) => updateField('instructorPayPerClass', parseOptionalNonNegativeNumber(event.target.value))}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className={cardClass}>
              <div className={cardBodyClass}>
                <div className="mb-4 flex items-center gap-3">
                  <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary text-white">
                    <i className="fa-regular fa-building" aria-hidden="true" />
                  </span>
                  <h2 className="text-xl font-semibold tracking-tight text-marketing-forest">
                    {cardsCopy.fixed_costs?.title}
                  </h2>
                </div>

                <div className="space-y-3">
                  <div className="grid gap-3 sm:grid-cols-2 sm:items-end">
                    <div>
                      <label className={labelClass}>{cardsCopy.fixed_costs?.rent_label}</label>
                      <div className={inputGroupClass}>
                        <span className={`${inputGroupAddonClass} border-r border-slate-200`}>{currencyPrefix(currency)}</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          className={inputGroupFieldClass}
                          value={normalizeNumberInputValue(formData.rentAmount)}
                          onChange={(event) => updateField('rentAmount', parseOptionalNonNegativeNumber(event.target.value))}
                        />
                      </div>
                    </div>

                    <div>
                      <label className={labelClass}>&nbsp;</label>
                      <select
                        className={textInputClass}
                        value={formData.rentFrequency}
                        onChange={(event) => updateField('rentFrequency', event.target.value)}
                      >
                        <option value="monthly">{cardsCopy.fixed_costs?.frequency_monthly}</option>
                        <option value="yearly">{cardsCopy.fixed_costs?.frequency_yearly}</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2 sm:items-end">
                    <div>
                      <label className={labelClass}>{cardsCopy.fixed_costs?.software_label}</label>
                      <div className={inputGroupClass}>
                        <span className={`${inputGroupAddonClass} border-r border-slate-200`}>{currencyPrefix(currency)}</span>
                        <input
                          type="text"
                          inputMode="decimal"
                          className={inputGroupFieldClass}
                          value={normalizeNumberInputValue(formData.softwareAmount)}
                          onChange={(event) => updateField('softwareAmount', parseOptionalNonNegativeNumber(event.target.value))}
                        />
                      </div>
                    </div>

                    <div>
                      <label className={labelClass}>&nbsp;</label>
                      <select
                        className={textInputClass}
                        value={formData.softwareFrequency}
                        onChange={(event) => updateField('softwareFrequency', event.target.value)}
                      >
                        <option value="monthly">{cardsCopy.fixed_costs?.frequency_monthly}</option>
                        <option value="yearly">{cardsCopy.fixed_costs?.frequency_yearly}</option>
                      </select>
                    </div>
                  </div>

                  <button
                    type="button"
                    className="inline-flex items-center gap-2 rounded-full px-1 py-1 text-sm font-semibold text-primary transition-colors hover:text-primary-700"
                    onClick={() => setShowAdvancedFixed((value) => !value)}
                  >
                    <span>{showAdvancedFixed ? cardsCopy.fixed_costs?.hide_additional : cardsCopy.fixed_costs?.show_additional}</span>
                    <i className={`fa-solid ${showAdvancedFixed ? 'fa-chevron-up' : 'fa-chevron-down'} text-xs`} aria-hidden="true" />
                  </button>

                  {showAdvancedFixed ? (
                    <div className="space-y-3 border-t border-slate-200 pt-3">
                      {formData.additionalOverhead.map((item) => (
                        <div key={item.id} className="grid gap-2 sm:grid-cols-[minmax(0,1.25fr)_minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end">
                          <div>
                            <label className={labelClass}>{cardsCopy.fixed_costs?.additional_label_placeholder}</label>
                            <input
                              type="text"
                              className={textInputClass}
                              placeholder={cardsCopy.fixed_costs?.additional_label_placeholder}
                              value={item.label}
                              onChange={(event) => updateOverheadItem(item.id, 'label', event.target.value)}
                            />
                          </div>

                          <div>
                            <label className={labelClass}>{cardsCopy.fixed_costs?.add_item}</label>
                            <div className={inputGroupClass}>
                              <span className={`${inputGroupAddonClass} border-r border-slate-200`}>{currencyPrefix(currency)}</span>
                              <input
                                type="text"
                                inputMode="decimal"
                                className={inputGroupFieldClass}
                                value={normalizeNumberInputValue(item.amount)}
                                onChange={(event) => updateOverheadItem(item.id, 'amount', parseOptionalNonNegativeNumber(event.target.value))}
                              />
                            </div>
                          </div>

                          <div>
                            <label className={labelClass}>&nbsp;</label>
                            <select
                              className={textInputClass}
                              value={item.frequency}
                              onChange={(event) => updateOverheadItem(item.id, 'frequency', event.target.value)}
                            >
                              <option value="monthly">{cardsCopy.fixed_costs?.frequency_monthly}</option>
                              <option value="yearly">{cardsCopy.fixed_costs?.frequency_yearly}</option>
                            </select>
                          </div>

                          <button
                            type="button"
                            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-red-200 bg-red-50 text-red-600 transition-colors hover:bg-red-100"
                            onClick={() => removeOverheadItem(item.id)}
                            aria-label={cardsCopy.fixed_costs?.hide_additional}
                          >
                            <i className="fa-solid fa-minus text-sm" aria-hidden="true" />
                          </button>
                        </div>
                      ))}

                      <button
                        type="button"
                        className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:border-primary/30 hover:bg-primary-50 hover:text-primary"
                        onClick={addOverheadItem}
                      >
                        <i className="fa-solid fa-plus" aria-hidden="true" />
                        <span>{cardsCopy.fixed_costs?.add_item}</span>
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            <div className="fixed inset-x-0 bottom-0 z-30 border-t border-slate-200 bg-white/95 p-3 shadow-[0_-10px_30px_-20px_rgba(15,23,42,0.35)] backdrop-blur lg:hidden">
              <div className="mx-auto max-w-7xl">
                <button
                  type="button"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-primary px-5 py-3 text-base font-semibold text-white transition-colors hover:bg-primary-700"
                  onClick={handleMobileCalculate}
                >
                  <i className="fa-solid fa-rotate-right" aria-hidden="true" />
                  {hasCalculated ? actionsCopy.recalculate : actionsCopy.calculate}
                </button>
              </div>
            </div>
          </div>

          <div className="lg:pt-10" id="breakeven-results">
            <div className="lg:sticky lg:top-28">
              {!hasCalculated ? (
                <div className="hidden min-h-[420px] items-center justify-center rounded-[1.75rem] border border-dashed border-slate-300 bg-white p-8 text-center lg:flex">
                  <div className="max-w-xs text-primary">
                    <i className="fa-solid fa-calculator text-4xl" aria-hidden="true" />
                    <div className="my-4 text-lg font-medium text-slate-700">{actionsCopy.placeholder}</div>
                    <button type="button" className="inline-flex items-center justify-center rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-primary-700" onClick={handleCalculateClick}>
                      {actionsCopy.calculate}
                    </button>
                  </div>
                </div>
              ) : result ? (
                <div className="space-y-4">
                  <div className="breakeven-result-hero relative overflow-hidden rounded-[1.75rem] p-6 text-white shadow-lg shadow-primary-950/10 sm:p-8">
                    <div className="breakeven-result-hero__glow" aria-hidden="true" />
                    <div className="relative">
                      <div className="mb-1 text-xs font-semibold uppercase tracking-[0.12em] text-white/80">
                        {resultsCopy.break_even_per_visit_label}
                      </div>
                      <div className="mb-2 text-4xl font-semibold tracking-tight sm:text-5xl">
                        {formatMoney(result.breakEvenPerVisit, currency)}
                      </div>
                      <div className="text-sm leading-6 text-white/80">
                        {(resultsCopy.break_even_help || '').replace('%{attendance}', String(formData.avgAttendance))}
                      </div>

                      <div className="my-5 border-t border-white/20" />

                      <div className="flex items-end justify-between gap-4">
                        <div>
                          <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-white/70">
                            {resultsCopy.true_cost_per_class_label}
                          </div>
                          <div className="text-2xl font-semibold tracking-tight sm:text-3xl">
                            {formatMoney(result.costPerClass, currency)}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-white/70">
                            {resultsCopy.monthly_total_label}
                          </div>
                          <div className="text-xl font-semibold tracking-tight sm:text-2xl">
                            {formatMoney(result.monthlyTotalCost, currency)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={cardClass}>
                    <div className="px-4 py-5 sm:px-6">
                      <h3 className="mb-4 text-lg font-semibold tracking-tight text-slate-900">{resultsCopy.breakdown_title}</h3>

                      <div className="space-y-4">
                        <div>
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2">
                              <span className="h-2.5 w-2.5 rounded-full bg-primary" />
                              <span className="text-sm text-slate-600">{resultsCopy.fixed_overhead_label}</span>
                            </div>
                            <span className="text-sm font-semibold text-slate-900">{formatMoney(perClassFixed, currency)}</span>
                          </div>
                          <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
                            <div className="h-full rounded-full bg-primary" style={{ width: `${Math.min(100, Math.max(0, fixedShare))}%` }} />
                          </div>
                        </div>

                        <div>
                          <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2">
                              <span className="h-2.5 w-2.5 rounded-full bg-emerald-600" />
                              <span className="text-sm text-slate-600">{resultsCopy.instructor_pay_label}</span>
                            </div>
                            <span className="text-sm font-semibold text-slate-900">{formatMoney(perClassInstructor, currency)}</span>
                          </div>
                          <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-100">
                            <div className="h-full rounded-full bg-emerald-600" style={{ width: `${Math.min(100, Math.max(0, instructorShare))}%` }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={cardClass}>
                    <div className="px-4 py-5 sm:px-6">
                      <h3 className="mb-3 text-lg font-semibold tracking-tight text-slate-900">{resultsCopy.levers_title}</h3>
                      <div className="space-y-2">
                        <button
                          type="button"
                          className="inline-flex w-full items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-left text-sm font-semibold text-slate-700 transition-colors hover:border-primary/30 hover:bg-primary-50 hover:text-primary"
                          onClick={() => updateField('classesPerWeek', formData.classesPerWeek + 1)}
                        >
                          <span>{resultsCopy.lever_add_class}</span>
                          <i className="fa-solid fa-arrow-right text-slate-400" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          className="inline-flex w-full items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-left text-sm font-semibold text-slate-700 transition-colors hover:border-primary/30 hover:bg-primary-50 hover:text-primary"
                          onClick={() => updateField('avgAttendance', formData.avgAttendance + 1)}
                        >
                          <span>{resultsCopy.lever_add_attendance}</span>
                          <i className="fa-solid fa-arrow-right text-slate-400" aria-hidden="true" />
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="hidden lg:block">
                    <button
                      type="button"
                      className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition-colors hover:border-primary/30 hover:bg-primary-50 hover:text-primary"
                      onClick={handleCalculateClick}
                    >
                      <i className="fa-solid fa-rotate-right text-primary" aria-hidden="true" />
                      {actionsCopy.recalculate}
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default BreakEvenCalculator
